import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PrototypeNav from './PrototypeNav';

interface PrototypeNavWrapperProps {
  currentScreen: string;
  onNavigate: (screen: string) => void;
}

const PrototypeNavWrapper: React.FC<PrototypeNavWrapperProps> = ({ currentScreen, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Update screen based on location when inside Router
  React.useEffect(() => {
    const path = location.pathname;
    
    // Main pages
    if (path === '/') onNavigate('home');
    else if (path === '/categories') onNavigate('categories');
    else if (path === '/calendar') onNavigate('calendar');
    else if (path === '/store') onNavigate('store');
    else if (path === '/login') onNavigate('login');
    else if (path === '/otp') onNavigate('otp');
    else if (path === '/profile') onNavigate('profile');
    else if (path === '/support') onNavigate('support');
    else if (path === '/forgot-password') onNavigate('forgot-password');
    else if (path === '/delete-account') onNavigate('delete-account');
    
    // Course pages
    else if (path.startsWith('/course/') && path.includes('/chapters') && !path.includes('/chapter/')) onNavigate('chapters-list');
    else if (path.startsWith('/course/') && path.includes('/chapter/') && path.includes('/video/')) onNavigate('video-player');
    else if (path.startsWith('/course/') && path.includes('/chapter/') && path.includes('/quiz/') && path.includes('/results')) onNavigate('quiz-results');
    else if (path.startsWith('/course/') && path.includes('/chapter/') && path.includes('/quiz/')) onNavigate('quiz-attempt');
    else if (path.startsWith('/course/') && path.includes('/chapter/')) onNavigate('chapter-details');
    else if (path.startsWith('/course/')) onNavigate('course-details');
    
    // Live session pages
    else if (path.startsWith('/live-session/') && path.includes('/room')) onNavigate('live-session-room');
    else if (path.startsWith('/live-session/')) onNavigate('live-session-details');
    else if (path === '/live-sessions' || path === '/live-sessions/') onNavigate('live-sessions-list');
    
    // Online course pages
    else if (path.startsWith('/online-course/') && path.includes('/session/') && path.includes('/room')) onNavigate('live-session-room');
    else if (path.startsWith('/online-course/') && path.includes('/session/')) onNavigate('session-details');
    else if (path.startsWith('/online-course/')) onNavigate('online-course-details');
    
    // Payment pages
    else if (path.startsWith('/payment/') && path.includes('/status')) onNavigate('payment-status');
    else if (path.startsWith('/payment/')) onNavigate('payment-proof');
    
    // Profile pages
    else if (path === '/profile/edit') onNavigate('edit-profile');
    else if (path === '/profile/courses') onNavigate('my-courses');
    else if (path === '/profile/quizzes') onNavigate('quizzes');
    else if (path === '/profile/books') onNavigate('my-books');
    else if (path === '/orders') onNavigate('orders');
    else if (path === '/profile/about') onNavigate('about');
    else if (path === '/profile/privacy') onNavigate('privacy');
    else if (path === '/settings') onNavigate('settings');
    
    // See All pages
    else if (path === '/courses/all') onNavigate('all-courses');
    else if (path === '/resources/all') onNavigate('all-resources');
    else if (path.startsWith('/resource/')) onNavigate('resource-details');
    else if (path === '/live-sessions/all') onNavigate('all-live-sessions');
  }, [location.pathname, onNavigate]);

  // Enhanced navigation handler that uses React Router
  const handleNavigateWithRouter = (screen: string, path?: string) => {
    onNavigate(screen);
    if (path) {
      if (location.pathname !== path) {
        navigate(path);
      }
    } else {
      // Fallback: use screen to determine path if path not provided
      const paths: { [key: string]: string } = {
        'home': '/',
        'categories': '/categories',
        'calendar': '/calendar',
        'store': '/store',
        'login': '/login',
        'otp': '/otp',
        'profile': '/profile',
        'support': '/support',
        'forgot-password': '/forgot-password',
        'delete-account': '/delete-account',
        'course-details': '/course/1',
        'chapters-list': '/course/1/chapters',
        'chapter-details': '/course/1/chapter/1',
        'video-player': '/course/1/chapter/1/video/1',
        'quiz-attempt': '/course/1/chapter/1/quiz/1',
        'quiz-results': '/course/1/chapter/1/quiz/1/results',
        'live-sessions-list': '/live-sessions',
        'live-session-details': '/live-session/1',
        'live-session-room': '/live-session/1/room',
        'payment-proof': '/payment/1/proof',
        'payment-status': '/payment/1/status',
        'edit-profile': '/profile/edit',
        'my-courses': '/profile/courses',
        'quizzes': '/profile/quizzes',
        'my-books': '/profile/books',
        'orders': '/orders',
        'about': '/profile/about',
        'privacy': '/profile/privacy',
        'settings': '/settings',
        'all-courses': '/courses/all',
        'all-resources': '/resources/all',
        'resource-details': '/resource/1',
        'all-live-sessions': '/live-sessions/all'
      };
      const targetPath = paths[screen];
      if (targetPath && location.pathname !== targetPath) {
        navigate(targetPath);
      }
    }
  };

  return <PrototypeNav currentScreen={currentScreen} onNavigate={handleNavigateWithRouter} />;
};

export default PrototypeNavWrapper;

